import React, { useState } from 'react';
import ProductCard from './ProductCard';
import ProductNewRibbon from './ProductNewRibbon';
import PriceWithVatLabel from './PriceWithVatLabel';
import LjudTieredProductGrid from './LjudTieredProductGrid';
import LjusTieredProductGrid from './LjusTieredProductGrid';
import MikrofonTieredProductGrid from './MikrofonTieredProductGrid';
import ScenTrossTieredProductGrid from './ScenTrossTieredProductGrid';
import EffekterTieredProductGrid from './EffekterTieredProductGrid';
import { Product } from '../types';
import { Search, SlidersHorizontal, ArrowUpDown, Grid, List } from 'lucide-react';
import type { ProductsListSortKey } from '../lib/productsListQuery';
import { trackBookingCtaClick } from '../utils/siteAnalytics';

interface ProductGridProps {
  products: Product[];
  selectedCategory?: string;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  sortBy: ProductsListSortKey;
  onSortChange: (value: ProductsListSortKey) => void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  totalCount: number | null;
}

const ProductGrid: React.FC<ProductGridProps> = ({
  products,
  selectedCategory = '',
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  hasMore,
  loadingMore,
  onLoadMore,
  totalCount,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const sortOptions: { value: ProductsListSortKey; label: string }[] = [
    { value: 'popular', label: 'Populärast först' },
    { value: 'name', label: 'Namn A-Ö' },
    { value: 'price_low', label: 'Lägsta pris först' },
    { value: 'price_high', label: 'Högsta pris först' },
    { value: 'category', label: 'Kategori' },
  ];

  const shown = products.length;
  const isLjudTiered = selectedCategory === 'Ljud';
  const isLjusTiered = selectedCategory === 'Ljus';
  const isMikrofonTiered = selectedCategory === 'Mikrofon';
  const isScenTrossTiered = selectedCategory === 'Scen / Tross';
  const isEffekterTiered = selectedCategory === 'Effekter';
  const isTieredCategory =
    isLjudTiered ||
    isLjusTiered ||
    isMikrofonTiered ||
    isScenTrossTiered ||
    isEffekterTiered;

  return (
    <div>
      <div className="mb-6 space-y-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search className="text-gray-400" size={20} />
          </div>
          <input
            type="text"
            className="bg-slate-900 border border-slate-800 text-white text-base rounded-none focus:ring-purple-500 focus:border-purple-500 block w-full pl-10 p-3"
            placeholder="Sök produkt..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        <div className="md:hidden">
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center bg-slate-900 border border-slate-800 text-slate-200 py-2 px-4 rounded-none"
          >
            <SlidersHorizontal size={18} className="mr-2" />
            Filter & Sortering
          </button>
        </div>

        <div className="hidden md:flex md:justify-between md:items-center gap-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <ArrowUpDown size={18} className="text-slate-500 mr-2" />
              <select
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value as ProductsListSortKey)}
                className="bg-slate-900 border border-slate-800 text-white text-sm rounded-none focus:ring-purple-500 focus:border-purple-500 p-2"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex bg-slate-900 border border-slate-800 rounded-none overflow-hidden">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-purple-600 text-white' : 'text-slate-300 hover:bg-slate-800'}`}
              >
                <Grid size={18} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-purple-600 text-white' : 'text-slate-300 hover:bg-slate-800'}`}
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>

        {showFilters && (
          <div className="md:hidden space-y-4 bg-slate-900 p-4 rounded-none border border-slate-800">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">Sortera efter</label>
              <select
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value as ProductsListSortKey)}
                className="w-full bg-slate-900 border border-slate-800 text-white text-sm rounded-none focus:ring-purple-500 focus:border-purple-500 p-2"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="mb-4 text-sm text-slate-400">
        Visar {shown}
        {totalCount != null ? ` av ${totalCount}` : ''} produkter
        {selectedCategory && selectedCategory !== 'Alla' && <span> i kategorin &quot;{selectedCategory}&quot;</span>}
        {searchQuery.trim() && <span> (sökning)</span>}
      </div>

      {products.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-lg text-slate-400">Inga produkter hittades. Försök med en annan sökning eller kategori.</p>
        </div>
      ) : isLjudTiered ? (
        <LjudTieredProductGrid products={products} viewMode={viewMode} />
      ) : isLjusTiered ? (
        <LjusTieredProductGrid products={products} viewMode={viewMode} />
      ) : isMikrofonTiered ? (
        <MikrofonTieredProductGrid products={products} viewMode={viewMode} />
      ) : isScenTrossTiered ? (
        <ScenTrossTieredProductGrid products={products} viewMode={viewMode} />
      ) : isEffekterTiered ? (
        <EffekterTieredProductGrid products={products} viewMode={viewMode} />
      ) : (
        <>
          <div className="md:hidden grid grid-cols-1 gap-4">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {viewMode === 'grid' ? (
            <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="hidden md:block space-y-4">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="bg-slate-900/35 border border-slate-800 overflow-hidden transition-colors hover:border-slate-700"
                >
                  <div className="flex flex-row">
                    <div className="relative w-32 h-24 bg-slate-900/20 border-r border-slate-700 flex-shrink-0 overflow-hidden">
                      <ProductNewRibbon newUntil={product.newUntil} size="sm" />
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    </div>

                    <div className="flex-1 p-4">
                      <div className="flex flex-row justify-between items-start">
                        <div className="flex-1 mr-4">
                          <div className="flex items-center mb-2">
                            <span className="inline-flex items-center bg-slate-800 border border-slate-700 text-slate-200 text-xs font-medium px-2 py-1 rounded-none mr-2">
                              {product.category}
                            </span>
                            {product.popular && (
                              <span className="inline-flex items-center bg-purple-600 text-white text-xs font-medium px-2 py-1 rounded-none">
                                Populär
                              </span>
                            )}
                          </div>

                          <h3 className="text-lg font-bold text-white mb-2 line-clamp-1">{product.name}</h3>
                          <p className="text-slate-400 text-sm line-clamp-2 leading-relaxed mb-3">{product.shortDescription}</p>

                          <div className="flex items-center justify-between">
                            <div>
                              {product.priceOnRequest ? (
                                <>
                                  <div className="text-xl font-bold text-purple-300">Enligt offert</div>
                                  <div className="text-xs text-slate-400">Begär offert</div>
                                </>
                              ) : (
                                <>
                                  <PriceWithVatLabel
                                    price={product.pricePerDay}
                                    valueClassName="text-xl font-bold text-white"
                                    labelClassName="text-xs text-slate-400"
                                  />
                                  <div className="text-xs text-slate-400">per dag</div>
                                </>
                              )}
                            </div>

                            <div className="flex flex-row gap-2">
                              <a
                                href={`/booking/${product.id}`}
                                onClick={() =>
                                  trackBookingCtaClick(product, undefined, {
                                    entry_point: 'list',
                                    list_name: 'ProductGrid',
                                  })
                                }
                                className="inline-flex justify-center items-center bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-none transition-colors text-sm"
                              >
                                Boka nu
                              </a>
                              <a
                                href={`/produkter/${product.slug || product.id}`}
                                className="inline-flex justify-center items-center bg-slate-900/20 hover:bg-slate-900/30 text-white font-medium py-2 px-4 rounded-none transition-colors duration-300 border border-slate-700 text-sm"
                              >
                                Detaljer
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {!isTieredCategory && hasMore && products.length > 0 && (
        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={() => void onLoadMore()}
            disabled={loadingMore}
            className="inline-flex items-center justify-center bg-purple-600 hover:bg-purple-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium py-3 px-10 rounded-none transition-colors"
          >
            {loadingMore ? 'Laddar…' : 'Visa fler'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductGrid;
